import api from "../sharedInterceptors";

api.defaults.baseURL = process.env.REACT_APP_MAPPER_MODEL_BASEURL_V1;

// Reads a ReadableStream fully into a string.
// Also tries to parse it as JSON in case the backend returns {"detail": "..."} etc.
async function readStreamAsText(stream) {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let text = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    text += decoder.decode(value, { stream: true });
  }
  return text;
}

async function parseErrorBody(text) {
  try {
    const parsed = JSON.parse(text);
    // normalize: prefer a message/detail field, fallback to the whole parsed object stringified
    return parsed.message || parsed.detail || parsed.error || text;
  } catch {
    // not JSON, just plain text
    return text;
  }
}

// Wraps a stream-returning API call: on error, drains the ReadableStream
// into a readable string so callers can inspect error.response.data as text.
async function callStreamEndpoint(requestFn) {
  try {
    return await requestFn();
  } catch (error) {
    const data = error?.response?.data;
    if (data && typeof data.getReader === "function") {
      try {
        const rawText = await readStreamAsText(data);
        error.response.data = await parseErrorBody(rawText);
      } catch (streamReadError) {
        console.error("Failed to read error stream:", streamReadError);
      }
    }
    throw error;
  }
}

class PhysicModelAPI {
  async postSimulationDataStream(formData, params) {
    return callStreamEndpoint(() =>
      api.post(
        `${process.env.REACT_APP_MAPPER_MODEL_BASEURL_V1}/prediction/simulation/stream/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: params,
          responseType: "stream",
          adapter: "fetch",
        },
      ),
    );
  }

  async postBuildingsData(buildingsFormData, id) {
    return await api.post(
      `${process.env.REACT_APP_MAPPER_MODEL_BASEURL_V1}/in-risk/building/`,
      buildingsFormData,
      {
        params: { id },
      },
    );
  }

  async putDynamicDataStream(formData, params) {
    return callStreamEndpoint(() =>
      api.put(
        `${process.env.REACT_APP_MAPPER_MODEL_BASEURL_V1}/prediction/dynamic/stream/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: params,
          responseType: "stream",
          adapter: "fetch",
        },
      ),
    );
  }

  async postDynamicDataStream(formData, params) {
    return callStreamEndpoint(() =>
      api.post(
        `${process.env.REACT_APP_MAPPER_MODEL_BASEURL_V1}/prediction/dynamic/stream/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: params,
          responseType: "stream",
          adapter: "fetch",
        },
      ),
    );
  }

  async putSimulationDataStream(formData, params) {
    return callStreamEndpoint(() =>
      api.put(
        `${process.env.REACT_APP_MAPPER_MODEL_BASEURL_V1}/prediction/simulation/stream/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: params,
          responseType: "stream",
          adapter: "fetch",
        },
      ),
    );
  }

  async getAllUserHistoricEventsApi() {
    return await api.get(
      `${process.env.REACT_APP_MAPPER_MODEL_BASEURL_V1}/prediction/get_myfires/`,
    );
  }

  async getHistoricEventByIdApi(fire_id) {
    return await api.get(
      `${process.env.REACT_APP_MAPPER_MODEL_BASEURL_V1}/prediction/fire_polygons/`,
      { params: { fire_id } },
    );
  }
}

export default PhysicModelAPI;